import { currentLanguage, onLanguageChange, setLabel } from './i18n';
import { createElement, setText, getUiRoot } from './dom';
import type { LoadingScreen } from './types';
import type { CitizenAsset } from '../world/life/citizens/citizenAsset';
import { createCitizenPreview } from './citizenPreview';
import { clothing, type Clothing } from './visitorAppearance';
import { createLanguageToggle } from './languageToggle';
import { CitizenSelection } from './citizenSelection';
import { createCitizenRegister } from './citizenRegister';
import { drawFabricSwatch } from './fabricSwatch';
import { createGatewaySettings } from './gatewaySettings';
import { createAccountLauncher } from './accountLauncher';
import type { Account } from '../account/account';
import { CITY_INTRODUCTION } from '../history/cityIntroduction';
import { sharedCityRequiresAccount } from '../multiplayer/access';
import { createOnlineVisitors } from './onlineVisitors';
import { createIcon } from './icons';

export function createLoadingScreen(account: Account, openAccount: () => void): LoadingScreen {
  return new EntryScreen(account, openAccount);
}

class EntryScreen implements LoadingScreen {
  private readonly screen = createElement('div', 'loading-screen', getUiRoot());
  private readonly progress = createElement('div', 'loading-progress', this.screen);
  private readonly bar = createElement('div', 'loading-bar', this.progress);
  private readonly label = createElement('div', 'loading-label', this.screen);
  private readonly selection = new CitizenSelection();
  private dispose = (): void => {};
  private rebuild = (): void => {};
  private open = true;
  private readonly navigation = createElement('nav', 'entry-tools', this.screen);
  readonly tours = createElement('div', 'entry-tours');
  private readonly settings = createGatewaySettings(this.navigation);
  readonly controls = this.settings.controls;
  private onEnter: Parameters<LoadingScreen['setReady']>[0] | null = null;
  private onResume: (() => void) | null = null;

  constructor(
    private readonly account: Account,
    private readonly openAccount: () => void
  ) {
    createAccountLauncher(this.navigation, account, openAccount);
    this.navigation.hidden = true;
    createLanguageToggle();
    document.body.classList.add('entry-open');
    this.screen.setAttribute('role', 'dialog');
    this.screen.setAttribute('aria-modal', 'true');
    this.screen.setAttribute('aria-labelledby', 'entry-title');
    const title = createElement('h1', 'loading-title');
    title.id = 'entry-title';
    setText(title, 'AMSTERDAM 1538');
    this.screen.prepend(title);
    const introduction = createElement('p', 'entry-introduction');
    setText(introduction, CITY_INTRODUCTION.en);
    title.after(introduction);
    setText(this.label, 'Loading...');
    window.addEventListener(
      'pagehide',
      () => {
        this.disposeMenu();
      },
      { once: true }
    );
  }

  setProgress(fraction: number, labelText: string): void {
    this.bar.style.transform = `scaleX(${Math.round(fraction * 100) / 100})`;
    setText(this.label, labelText);
  }

  setReady(
    onEnter: Parameters<LoadingScreen['setReady']>[0],
    multiplayer: boolean,
    asset: CitizenAsset
  ): void {
    this.setProgress(1, 'Ready');
    this.onEnter = onEnter;
    this.screen.classList.add('ready');
    this.progress.hidden = this.label.hidden = true;
    const library = createElement('a', 'reference-library', this.tours);
    const updateLibrary = (): void => {
      const base = import.meta.env.DEV ? 'http://localhost:5192/' : '/library/';
      library.href = `${base}${currentLanguage() === 'nl' ? 'nl/' : ''}`;
    };
    updateLibrary();
    window.addEventListener('pagehide', onLanguageChange(updateLibrary), { once: true });
    library.target = '_blank';
    library.rel = 'noopener';
    const icon = createIcon('book', 16);
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('stroke-width', '1.5');
    library.append(icon);
    setText(createElement('span', '', library), 'Library');
    setLabel(library, 'title', 'Library');
    this.rebuild = () => {
      this.dispose = createEntryMenu(this.screen, onEnter, multiplayer, asset, this.selection, {
        account: this.account,
        open: this.openAccount,
      });
      this.screen.querySelector('.loading-actions')?.append(this.tours);
    };
    this.rebuild();
    this.screen.append(this.navigation);
    this.navigation.hidden = false;
  }

  hide(): void {
    if (!this.open) return;
    this.settings.close();
    for (const panel of this.screen.querySelectorAll<HTMLDialogElement>('dialog[open]'))
      panel.close();
    this.screen.append(this.tours);
    this.disposeMenu();
    this.open = false;
    document.body.classList.remove('entry-open');
    this.screen.hidden = true;
    this.screen.inert = true;
  }

  private disposeMenu(): void {
    const dispose = this.dispose;
    this.dispose = () => {};
    dispose();
  }

  show(onResume: () => void): void {
    if (this.open) return;
    this.open = true;
    this.onResume = onResume;
    this.rebuild();
    this.screen.append(this.navigation);
    const resume = createElement('button', 'entry-resume', this.screen);
    resume.type = 'button';
    setText(resume, 'Return to the city');
    resume.addEventListener('click', onResume);
    document.body.classList.add('entry-open');
    this.screen.hidden = false;
    this.screen.inert = false;
    resume.focus();
  }

  get isOpen(): boolean {
    return this.open;
  }

  enter(mode: 'single' | 'multi'): void {
    if (!this.onEnter) throw new Error('City entry is not ready');
    this.onEnter(mode, this.selection.character, this.selection.name, this.selection.colour);
  }

  resume(): void {
    if (this.open) this.onResume?.();
  }
}

function createFigures(
  parent: HTMLElement,
  asset: CitizenAsset,
  selection: CitizenSelection,
  previews: ReturnType<typeof createCitizenPreview>[],
  updateNames: () => void
): void {
  const cards: HTMLButtonElement[] = [];
  for (const value of ['male', 'female'] as const) {
    const card = createElement('button', 'citizen-card', parent);
    card.type = 'button';
    card.dataset.character = value;
    card.setAttribute('aria-pressed', String(selection.character === value));
    const canvas = createElement('canvas', 'citizen-preview', card);
    canvas.setAttribute('aria-hidden', 'true');
    setText(createElement('span', 'citizen-caption', card), value === 'male' ? 'Male' : 'Female');
    const preview = createCitizenPreview(canvas, asset, value);
    previews.push(preview);
    preview.selected(selection.character === value);
    preview.colour(selection.colour);
    cards.push(card);
    card.addEventListener('click', () => {
      selection.character = value;
      for (const item of cards) item.setAttribute('aria-pressed', String(item === card));
      previews.forEach((item, index) => {
        item.selected(index === (value === 'male' ? 0 : 1));
      });
      updateNames();
    });
  }
}

function createClothing(
  fields: HTMLElement,
  selection: CitizenSelection,
  previews: ReturnType<typeof createCitizenPreview>[]
): void {
  const colours = createElement('fieldset', 'citizen-colours', fields);
  setText(createElement('legend', '', colours), 'Clothing');
  const swatches = createElement('div', 'clothing-swatches', colours);
  const buttons: HTMLButtonElement[] = [];
  for (const [value, item] of Object.entries(clothing)) {
    const button = createElement('button', 'clothing-swatch', swatches);
    button.type = 'button';
    button.dataset.clothing = value;
    setLabel(button, 'aria-label', item.label);
    button.setAttribute('aria-pressed', String(selection.colour === value));
    const fabric = createElement('canvas', 'fabric-swatch', button);
    fabric.setAttribute('aria-hidden', 'true');
    drawFabricSwatch(fabric, item.colour);
    buttons.push(button);
    button.addEventListener('click', () => {
      selection.colour = value as Clothing;
      for (const preview of previews) preview.colour(selection.colour);
      for (const other of buttons) other.setAttribute('aria-pressed', String(other === button));
    });
  }
}

interface EntryAccount {
  readonly account: Account;
  readonly open: () => void;
}

/** Admission failures belong beside Join; optional accounts must not block guest entry. */
function createSharedCityHint(menu: HTMLElement, { account }: EntryAccount, multiplayer: boolean) {
  const hint = createElement('p', 'loading-mode-hint', menu);
  hint.id = 'shared-city-hint';
  hint.hidden = true;
  const follow = (): void => {
    if (!multiplayer || account.state.kind === 'signedIn') hint.hidden = true;
  };
  follow();
  return { hint, stop: account.onChange(follow) };
}

async function allowSharedEntry(
  access: EntryAccount,
  hint: HTMLElement,
  disposed: () => boolean
): Promise<boolean> {
  const signedIn = (): boolean => access.account.state.kind === 'signedIn';
  if (signedIn()) return true;
  let required: boolean;
  try {
    required = await sharedCityRequiresAccount();
  } catch {
    if (disposed()) return false;
    setText(hint, 'The shared city is unavailable. Try again.');
    hint.setAttribute('role', 'alert');
    hint.hidden = false;
    return false;
  }
  if (disposed()) return false;
  if (required && !signedIn()) {
    setText(hint, 'Sign in to join the shared city.');
    hint.hidden = false;
    access.open();
    return false;
  }
  return true;
}

function createActions(
  menu: HTMLElement,
  onEnter: Parameters<LoadingScreen['setReady']>[0],
  multiplayer: boolean,
  selection: CitizenSelection,
  access: EntryAccount
): () => void {
  const actions = createElement('div', 'loading-actions', menu);
  const { hint, stop } = createSharedCityHint(menu, access, multiplayer);
  let stopVisitors = (): void => {};
  let disposed = false;
  for (const [mode, text] of [
    ['single', 'Explore on your own'],
    ['multi', 'Join the shared city'],
  ] as const) {
    const button = createElement('button', 'loading-mode', actions);
    button.type = 'button';
    button.dataset.mode = mode;
    setText(button, text);
    button.disabled = mode === 'multi' && !multiplayer;
    if (button.disabled)
      setLabel(
        button,
        'title',
        'The shared city requires a live session with the standard city layout'
      );
    if (mode === 'multi') {
      setLabel(button, 'aria-label', text);
      button.setAttribute('aria-describedby', hint.id);
      if (multiplayer) stopVisitors = createOnlineVisitors(button);
    }
    button.addEventListener('click', () => {
      void (async () => {
        for (const action of actions.querySelectorAll<HTMLButtonElement>('.loading-mode'))
          action.disabled = true;
        hint.hidden = true;
        hint.removeAttribute('role');
        if (mode === 'multi' && !(await allowSharedEntry(access, hint, () => disposed))) {
          if (!disposed)
            for (const action of actions.querySelectorAll<HTMLButtonElement>('.loading-mode'))
              action.disabled = action.dataset.mode === 'multi' && !multiplayer;
          return;
        }
        if (!disposed) onEnter(mode, selection.character, selection.name, selection.colour);
      })();
    });
  }
  return () => {
    disposed = true;
    stopVisitors();
    stop();
  };
}

function createEntryMenu(
  screen: HTMLElement,
  onEnter: Parameters<LoadingScreen['setReady']>[0],
  multiplayer: boolean,
  asset: CitizenAsset,
  selection: CitizenSelection,
  access: EntryAccount
) {
  const menu = createElement('div', 'loading-modes', screen);
  setText(createElement('h2', 'entry-heading', menu), 'Choose your citizen');
  const figures = createElement('div', 'citizen-figures', menu);
  const fields = createElement('div', 'citizen-fields', menu);
  const previews: ReturnType<typeof createCitizenPreview>[] = [];
  const register = createCitizenRegister(fields, selection);
  createFigures(figures, asset, selection, previews, register.update);
  createClothing(fields, selection, previews);
  const stopActions = createActions(menu, onEnter, multiplayer, selection, access);
  return () => {
    stopActions();
    for (const preview of previews) preview.dispose();
    register.dispose();
    menu.remove();
    screen.querySelector('.entry-resume')?.remove();
  };
}
